import React, { FC, MouseEvent, useMemo } from 'react';
import { addProtocol } from '../../../../utils/addProtocol';
import { buildImageSource, buildImageSourceSet } from '../../../../utils/buildImageSource';
import { formatCurrency } from '../../../../utils/format/formatCurrency';
import { TransitionType, makeTransition } from '../../../../utils/styles/makeTransition';
import { useTranslation } from '../../../../utils/useTranslation';
import { IconForward } from '../../../icon/IconForward';
import { IconNewTab } from '../../../icon/IconNewTab';
import { IconProduct } from '../../../icon/IconProduct';
import { Button } from '../../../input/Button';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Flex } from '../../../layout/Flex';
import { Image } from '../../../media/Image';
import { Retry } from '../../../media/Retry';
import { Typography } from '../../../typography/Typography';
import { ClickOut, PostUI } from '../../types';
import { PRODUCT_BUTTON_TRANSITION_TIMING } from '../AnimateFooter/constants';
import {
    ProductContent,
    ProductTextContainer,
    ProductContainer,
    ProductTitle,
    MainButton,
    ProductMediaCover,
    ButtonBox,
    ProductMinizeButton,
} from './styles';

interface PostButtonProps {
    clickOut: ClickOut | undefined;
    isExpanded: boolean;
    onClickOutClick: (event: MouseEvent<HTMLElement>) => void;
    products: PostUI['products'];
    isProductExpanded: boolean;
    transitionTiming: number;
    onProductButtonClick: VoidFunction;
}

const MAX_PRODUCTS_PICTURES = 5;

export const PostButton: FC<PostButtonProps> = ({
    onClickOutClick,
    isExpanded,
    clickOut,
    products,
    isProductExpanded,
    transitionTiming,
    onProductButtonClick,
}) => {
    const t = useTranslation();

    const productView = useMemo(() => {
        if (!products.items.length) {
            return null;
        }

        if (products.items.length === 1) {
            const {
                priceInfo: { integerPart, fractionalPart },
                content: [{ media }],
                title,
            } = products.items[0];

            return (
                <>
                    <ProductMediaCover>
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            padding={0.5}
                            backgroundColor="white"
                        >
                            <Retry>
                                <Image
                                    width="100%"
                                    height="100%"
                                    src={buildImageSource(media)}
                                    srcSet={buildImageSourceSet(media)}
                                />
                            </Retry>
                        </Flex>
                    </ProductMediaCover>
                    <ProductTextContainer>
                        <ProductTitle>{t(title)}</ProductTitle>
                        <Typography
                            weight="medium"
                            variant="caption"
                            color="black"
                            textTransform="none"
                        >
                            {formatCurrency(integerPart, fractionalPart)}
                        </Typography>
                    </ProductTextContainer>
                </>
            );
        }

        const productsToView =
            products.items.length > MAX_PRODUCTS_PICTURES
                ? products.items.slice(0, MAX_PRODUCTS_PICTURES)
                : products.items;

        return (
            <>
                <>
                    {productsToView.map(({ content: [{ media }] }, index) => (
                        <ProductMediaCover index={index} key={index}>
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                padding={0.5}
                                backgroundColor="white"
                            >
                                <Retry>
                                    <Image
                                        width="100%"
                                        height="100%"
                                        src={buildImageSource(media)}
                                        srcSet={buildImageSourceSet(media)}
                                    />
                                </Retry>
                            </Flex>
                        </ProductMediaCover>
                    ))}
                </>
                <ProductTextContainer indexOffset={productsToView.length - 1}>
                    <ProductTitle>{t(products.title)}</ProductTitle>
                    <Typography
                        weight="medium"
                        variant="caption"
                        color="black"
                        textTransform="none"
                    >
                        {t(products.description)}
                    </Typography>
                </ProductTextContainer>
            </>
        );
    }, [products, isProductExpanded]);

    if (products.items.length) {
        return (
            <ButtonBox
                isExpanded={isExpanded}
                isProductExpanded={isProductExpanded}
                transition={makeTransition({
                    ms: transitionTiming,
                    styles: ['width', 'height'],
                    type:
                        transitionTiming === PRODUCT_BUTTON_TRANSITION_TIMING
                            ? TransitionType.EASE_OUT
                            : TransitionType.LINEAR,
                })}
            >
                <MainButton onClick={onProductButtonClick}>
                    <ProductMinizeButton
                        isProductExpanded={isProductExpanded}
                        transition={makeTransition({
                            ms: isProductExpanded ? 0 : transitionTiming,
                            styles: ['opacity'],
                            type: TransitionType.EASE_OUT,
                        })}
                    >
                        <Box mr="10px">
                            <IconProduct fontSize="16px" />
                        </Box>
                        {t(products.buttonTitle)}
                    </ProductMinizeButton>
                    <ProductContainer
                        isProductExpanded={isProductExpanded}
                        transition={makeTransition({
                            ms: isProductExpanded ? transitionTiming : 0,
                            styles: ['opacity'],
                            type: TransitionType.EASE_OUT,
                        })}
                    >
                        <ProductContent>{productView}</ProductContent>
                        <IconForward fontSize="16px" />
                    </ProductContainer>
                </MainButton>
            </ButtonBox>
        );
    }

    if (clickOut) {
        return (
            <ButtonBox
                isExpanded={isExpanded}
                dataTestId={DataTestId.CLICK_OUT}
                transition={makeTransition({
                    ms: transitionTiming,
                    styles: ['width', 'height'],
                })}
            >
                <Button
                    as="a"
                    target="_blank"
                    rel="nofollow noreferrer"
                    flexGrow={1}
                    href={addProtocol(clickOut.url)}
                    color="hardLight"
                    onClick={onClickOutClick}
                    width="100%"
                    fontSize="1rem"
                >
                    {clickOut.text}
                    <Box ml={1}>
                        <IconNewTab fontSize={14} color="#000" />
                    </Box>
                </Button>
            </ButtonBox>
        );
    }

    return null;
};
